import React, { useState } from 'react';
import { X, Loader2, Send, MessageSquareHeart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { AppUser } from '../types';
import { submitOffboardingSurvey, OffboardingSurveyType } from '../services/firebaseService';

interface OffboardingSurveyModalProps {
  user: AppUser;
  type: OffboardingSurveyType;
  provider?: string;
  /** Called after the survey is submitted OR skipped — parent should continue with the real action. */
  onComplete: () => void;
  /** Called when the user backs out entirely (X / backdrop) — the real action should be aborted. */
  onDismiss: () => void;
}

const REASON_KEYS = [
  'too_expensive',
  'not_using',
  'missing_features',
  'better_alternative',
  'technical_issues',
  'just_testing',
  'other',
] as const;

const RETURN_KEYS = [
  'lower_price',
  'more_spots',
  'more_features',
  'better_quality',
  'nothing',
  'other',
] as const;

const OffboardingSurveyModal: React.FC<OffboardingSurveyModalProps> = ({
  user, type, provider, onComplete, onDismiss,
}) => {
  const { t } = useTranslation();
  const trapRef = useFocusTrap<HTMLDivElement>();
  const [reason, setReason] = useState<string>('');
  const [wouldReturn, setWouldReturn] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitOffboardingSurvey(user, { type, reason, wouldReturn, comment, provider });
    } catch (error) {
      // Never block the user's action because feedback failed to save.
      console.error('Error submitting offboarding survey:', error);
    } finally {
      setIsSubmitting(false);
      onComplete();
    }
  };

  const handleSkip = () => {
    if (isSubmitting) return;
    onComplete();
  };

  const title = type === 'deletion'
    ? t('offboarding.title_deletion', 'Før du sletter kontoen')
    : t('offboarding.title_cancellation', 'Før du sier opp');
  const intro = type === 'deletion'
    ? t('offboarding.intro_deletion', 'Vi blir lei oss for å se deg gå. Tre raske spørsmål hjelper oss å bli bedre.')
    : t('offboarding.intro_cancellation', 'Vi blir lei oss for å se deg gå. Tre raske spørsmål hjelper oss å bli bedre.');

  const RadioGroup: React.FC<{
    label: string;
    options: readonly string[];
    prefix: string;
    value: string;
    onChange: (v: string) => void;
  }> = ({ label, options, prefix, value, onChange }) => (
    <div className="space-y-2">
      <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{label}</p>
      <div className="space-y-1.5">
        {options.map((key) => {
          const selected = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(selected ? '' : key)}
              className={`w-full text-left px-3.5 py-2.5 rounded-xl text-[12px] font-bold leading-snug transition-all active:scale-[0.99] border ${
                selected
                  ? 'bg-emerald-500/10 border-emerald-500/40 text-[var(--text-primary)]'
                  : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] text-[var(--text-secondary)] hover:border-[var(--border-secondary)]'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? 'border-emerald-500' : 'border-[var(--border-secondary)]'}`}>
                  {selected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                </span>
                {t(`offboarding.${prefix}.${key}`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10004] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { if (!isSubmitting) onDismiss(); }} />
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl shadow-3xl overflow-hidden animate-in zoom-in-95 fade-in duration-200 max-h-[88vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-5 pb-3 flex items-start justify-between gap-4 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
              <MessageSquareHeart size={20} />
            </div>
            <div className="space-y-0.5">
              <h3 className="text-base font-black text-[var(--text-primary)] tracking-tight leading-tight">{title}</h3>
              <p className="text-tiny text-[var(--text-secondary)] leading-snug">{intro}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (!isSubmitting) onDismiss(); }}
            disabled={isSubmitting}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors disabled:opacity-60 shrink-0"
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Questions */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5" style={{ overscrollBehavior: 'contain' }}>
          <RadioGroup
            label={type === 'deletion'
              ? t('offboarding.q_reason_deletion', 'Hva er hovedgrunnen til at du sletter kontoen?')
              : t('offboarding.q_reason_cancellation', 'Hva er hovedgrunnen til at du sier opp?')}
            options={REASON_KEYS}
            prefix="reason"
            value={reason}
            onChange={setReason}
          />

          <RadioGroup
            label={t('offboarding.q_return', 'Hva skulle til for at du ble værende eller kom tilbake?')}
            options={RETURN_KEYS}
            prefix="return"
            value={wouldReturn}
            onChange={setWouldReturn}
          />

          <div className="space-y-2">
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
              {t('offboarding.q_comment', 'Noe mer du vil dele med oss?')}
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder={t('offboarding.comment_placeholder', 'Valgfritt – din tilbakemelding er gull verdt for oss.')}
              className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] rounded-xl px-3.5 py-3 text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-emerald-500/40 transition-colors"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 pt-3 border-t border-[var(--border-primary)] flex gap-3">
          <button
            type="button"
            onClick={handleSkip}
            disabled={isSubmitting}
            className="flex-1 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-[var(--text-secondary)] py-3 rounded-2xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-60"
          >
            {t('offboarding.skip', 'Hopp over')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-2xl text-2xs font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={14} />}
            {t('offboarding.submit', 'Send svar')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffboardingSurveyModal;
